/**
 * Pestaña "Ficha" de /org/soluciones/:slug (03/11/2026): qué hace la solución, servicios,
 * cómo trabaja, roles, cómo pedirle trabajo y links. La editan el dueño de la solución y el
 * roster (lápiz por sección, mismo tablero que el Inicio de Owned Media); el roster asigna
 * el dueño. La de Owned Media es su Inicio: acá se lee y el botón lleva a editarla allá.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink, History, Plus, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SectionContentBoard } from '@/components/section-content/SectionContentBoard';
import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { OrgSheetProposal } from '@/components/org/OrgSheetProposal';
import { useOrgCommunityPicker, useOrgSheetHistory, useSaveOrgSheet, useSetOrgSolutionOwner } from '@/hooks/useOrg';
import { toastError } from '@/lib/toast-error';
import { formatDateTime } from '@/lib/utils';
import type { RouterOutputs } from '@/lib/api-types';
import type { Section } from '@/../../shared/section-content';

const k = 'org.sheet';
/** Dónde se edita la ficha que sale del apartado de un equipo (`edit_oml_home`, `edit_people_landing`, `edit_partnerships_hub`). */
const EXTERNAL_LABEL: Record<string, string> = {
  'oml-home': 'Se edita en el Inicio de Owned Media',
  'people-landing': 'Se edita en la landing de People',
  'partnerships-hub': 'Se edita en el Partnerships Hub',
};
type Sheet = RouterOutputs['org']['solution']['sheet'];

export function OrgSolutionSheet({ slug, sheet, isAdmin }: { slug: string; sheet: Sheet; isAdmin: boolean }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const save = useSaveOrgSheet(slug);
  const setOwner = useSetOrgSolutionOwner(slug);
  const history = useOrgSheetHistory(slug, historyOpen);
  const members = useOrgCommunityPicker(isAdmin && ownerOpen);

  const persist = (next: Section[]) => save.mutate({ slug, sections: next, prevUpdatedAt: sheet.updatedAt }, {
    onSuccess: () => { toast.success(t(`${k}.saved`, 'Ficha actualizada')); setEditing(null); },
    onError: (e) => { toastError(e, t(`${k}.save_failed`, 'No se pudo guardar la ficha')); if (e.data?.code === 'CONFLICT') setEditing(null); },
  });
  const { filled, total } = sheet.completeness;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant={filled === total ? 'default' : 'secondary'}>{t(`${k}.completeness`, '{{filled}} de {{total}} secciones completas', { filled, total })}</Badge>
          <span>{sheet.ownerEmail ? t(`${k}.owner`, 'Dueño: {{email}}', { email: sheet.ownerEmail }) : t(`${k}.no_owner`, 'Sin dueño asignado')}</span>
          {sheet.updatedAt && <span>· {t(`${k}.updated`, 'Actualizada {{at}} por {{by}}', { at: formatDateTime(sheet.updatedAt), by: sheet.updatedBy ?? '—' })}</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          {sheet.editPath && <Button size="sm" variant="outline" asChild><Link to={sheet.editPath}><ExternalLink className="h-4 w-4 mr-1" />{t(`${k}.edit_${sheet.source.replace('-', '_')}`, EXTERNAL_LABEL[sheet.source] ?? sheet.editPath)}</Link></Button>}
          {sheet.source === 'org' && <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(true)}><History className="h-4 w-4 mr-1" />{t(`${k}.history`, 'Historial')}</Button>}
          {isAdmin && sheet.source === 'org' && <Button size="sm" variant="ghost" onClick={() => setOwnerOpen(true)}><UserCog className="h-4 w-4 mr-1" />{t(`${k}.set_owner`, 'Asignar dueño')}</Button>}
          {sheet.canEdit && <Button size="sm" variant="outline" onClick={() => setEditing(sheet.sections.length)}><Plus className="h-4 w-4 mr-1" />{t('sectionContent.add_section', 'Agregar sección')}</Button>}
        </div>
      </div>

      <OrgSheetProposal key={slug} slug={slug} enabled={sheet.canEdit} />

      <SectionContentBoard
        sections={sheet.sections}
        canEdit={sheet.canEdit}
        saving={save.isPending}
        onPersist={persist}
        audienceNote={t(`${k}.no_secrets`, 'La ficha la lee cualquier Winclapper, la API y las IAs conectadas: nada de contraseñas, precios, costos ni datos de clientes.')}
        editing={editing}
        onEditingChange={setEditing}
        emptySection={<p className="text-sm italic text-muted-foreground">{sheet.canEdit ? t(`${k}.empty_edit`, 'Sin completar: tocá el lápiz para cargarla.') : t(`${k}.empty`, 'Sin completar todavía.')}</p>}
      />

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t(`${k}.history_title`, 'Quién editó la ficha')}</DialogTitle></DialogHeader>
          {history.isLoading && <p className="text-sm text-muted-foreground">{t('common.ui.loading', 'Cargando…')}</p>}
          {history.isError && <p className="text-sm text-destructive">{t(`${k}.history_error`, 'No se pudo cargar el historial')}</p>}
          {history.data && history.data.length === 0 && <p className="text-sm text-muted-foreground">{t(`${k}.history_empty`, 'Todavía nadie la editó.')}</p>}
          <ul className="space-y-1 text-sm">
            {history.data?.map((v) => <li key={v.updatedAt} className="flex justify-between gap-3"><span>{formatDateTime(v.updatedAt)}</span><span className="text-muted-foreground">{v.updatedBy}</span></li>)}
          </ul>
        </DialogContent>
      </Dialog>

      {isAdmin && (
        <MemberPickerDialog
          open={ownerOpen}
          onOpenChange={setOwnerOpen}
          title={t(`${k}.owner_title`, 'Dueño de la ficha')}
          description={t(`${k}.owner_desc`, 'Quien edita la ficha de esta solución (además del roster del Org Service).')}
          searchPlaceholder={t('common.search', 'Buscar…')}
          noResultsText={t('org.home.no_results', 'Sin resultados')}
          members={members.data ?? []}
          loading={members.isLoading}
          exclude={new Set(sheet.ownerEmail ? [sheet.ownerEmail.toLowerCase()] : [])}
          onPick={(m) => !setOwner.isPending && setOwner.mutate({ slug, email: m.email }, {
            onSuccess: () => { toast.success(t(`${k}.owner_saved`, 'Dueño asignado')); setOwnerOpen(false); },
            onError: (e) => toastError(e, t(`${k}.owner_failed`, 'No se pudo asignar el dueño')),
          })}
        >
          {sheet.ownerEmail && (
            <Button variant="ghost" size="sm" className="text-destructive" disabled={setOwner.isPending} onClick={() => setOwner.mutate({ slug, email: null }, { onSuccess: () => { toast.success(t(`${k}.owner_cleared`, 'Dueño quitado')); setOwnerOpen(false); }, onError: (e) => toastError(e, t(`${k}.owner_failed`, 'No se pudo asignar el dueño')) })}>
              {t(`${k}.owner_clear`, 'Quitar dueño')}
            </Button>
          )}
        </MemberPickerDialog>
      )}
    </div>
  );
}
